package music.jammers.client.shared.event.presentation.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyListState
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.FloatingActionButton
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.DialogProperties
import kotlinx.coroutines.launch
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.dialog_cancel
import limasoul.client.shared.generated.resources.dialog_ok
import limasoul.client.shared.generated.resources.event_detail_confirm_member_cancellation_message
import limasoul.client.shared.generated.resources.event_detail_confirm_member_cancellation_title
import limasoul.client.shared.generated.resources.event_detail_confirm_member_message
import limasoul.client.shared.generated.resources.event_detail_confirm_member_title
import limasoul.client.shared.generated.resources.event_detail_screen_acceptance_optional
import limasoul.client.shared.generated.resources.event_detail_screen_acceptance_title
import limasoul.client.shared.generated.resources.event_detail_screen_menu_edit
import limasoul.client.shared.generated.resources.event_detail_screen_menu_set_list
import limasoul.client.shared.generated.resources.event_detail_screen_optional_warning
import limasoul.client.shared.generated.resources.event_detail_screen_title
import limasoul.client.shared.generated.resources.event_freeze_screen_backlog_header
import limasoul.client.shared.generated.resources.event_freeze_screen_frozen_hint
import music.jammers.client.shared.component.CenterBox
import music.jammers.client.shared.component.DateFormatter
import music.jammers.client.shared.component.EVENT_DETAILS_FORMAT
import music.jammers.client.shared.component.ErrorDialog
import music.jammers.client.shared.component.JammersTopAppBar
import music.jammers.client.shared.component.Progress
import music.jammers.client.shared.component.avatar.presentation.ui.AvatarImageComponent
import music.jammers.client.shared.component.track.presentation.ui.TrackCard
import music.jammers.client.shared.event.di.EventComponent
import music.jammers.client.shared.event.presentation.viewmodel.EventDetailsItem
import music.jammers.client.shared.event.presentation.viewmodel.EventDetailsState
import music.jammers.client.shared.event.presentation.viewmodel.EventDetailsViewModel
import org.jetbrains.compose.resources.stringResource

const val EVENT_DETAILS_SCREEN = "EventDetailsScreen"

@Composable
fun EventDetailsScreen(
    eventId: Long,
) {
    val viewModel = remember { EventComponent.eventDetailViewModel.apply { onBind(eventId) } }
    val scrollState = rememberLazyListState(0, 0)
    val state = viewModel.state.value

    val snackbarCoroutineScope = rememberCoroutineScope()
    val snackbarHostState = remember { SnackbarHostState() }

    val snackbarMessage = stringResource(Res.string.event_detail_screen_optional_warning)

    LaunchedEffect(Unit) {
        viewModel.snackBarState.collect {
            snackbarCoroutineScope.launch {
                snackbarHostState.showSnackbar(snackbarMessage)
            }
        }
    }

    Scaffold(
        topBar = {
            JammersTopAppBar(
                text = state.title ?: stringResource(Res.string.event_detail_screen_title),
                navigationIcon = {
                    IconButton(onClick = { viewModel.onBackPressed() }) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                },
                actions = {
                    AvatarImageComponent()

                    if (viewModel.state.value.user?.isAdmin == true) {
                        var showMenu by remember { mutableStateOf(false) }

                        IconButton(
                            onClick = { showMenu = true },
                        ) {
                            Icon(Icons.Filled.MoreVert, contentDescription = "Menu")
                        }

                        DropdownMenu(expanded = showMenu, onDismissRequest = { showMenu = false }) {
                            DropdownMenuItem(
                                text = {
                                    Text(stringResource(Res.string.event_detail_screen_menu_edit))
                                },
                                onClick = {
                                    showMenu = false
                                    viewModel.onEditClicked()
                                },
                            )

                            DropdownMenuItem(
                                text = {
                                    Text(stringResource(Res.string.event_detail_screen_menu_set_list))
                                },
                                onClick = {
                                    showMenu = false
                                    viewModel.onCommitTrackListClicked()
                                },
                            )
                        }
                    }
                },
            )
        },
        snackbarHost = {
            SnackbarHost(snackbarHostState)
        },
        floatingActionButton = {
            if (!state.frozen) {
                FloatingActionButton(
                    onClick = { viewModel.onTrackAddClicked() },
                    shape = CircleShape,
                ) {
                    Icon(
                        imageVector = Icons.Filled.Add,
                        contentDescription = "Add event",
                    )
                }
            }
        },
    ) { paddingValues: PaddingValues ->
        CenterBox {
            if (state.progress) {
                Progress()
            } else {
                Column(
                    modifier = Modifier.padding(paddingValues)
                ) {
                    Content(state, viewModel, scrollState)
                }
            }
        }
    }
}

@Composable
private fun Content(
    state: EventDetailsState,
    viewModel: EventDetailsViewModel,
    lazyListState: LazyListState,
) {
    state.error?.let { error ->
        ErrorDialog(error.message ?: "error") {
            viewModel.onErrorShown()
        }
    }

    state.confirmSignInTrack?.let { (track, eventMember) ->
        ConfirmAcceptMemberDialog(
            trackName = track.name,
            artistName = track.artist.name,
            instrumentName = eventMember.instrumentType.instrument.name,
            onPositiveClicked = {
                viewModel.onSignInPositive(eventMember)
            },
            onNegativeClicked = {
                viewModel.onDialogNegative()
            }
        )
    }

    state.confirmSignOutTrack?.let { (track, eventMember) ->
        ConfirmCancelMemberDialog(
            trackName = track.name,
            artistName = track.artist.name,
            instrumentName = eventMember.instrumentType.instrument.name,
            onPositiveClicked = {
                viewModel.onSignOutPositive(eventMember)
            },
            onNegativeClicked = {
                viewModel.onDialogNegative()
            }
        )
    }

    if (state.frozen) {
        Column(
            modifier = Modifier.fillMaxWidth()
                .background(MaterialTheme.colorScheme.surface)
        ) {
            Spacer(modifier = Modifier.size(16.dp))

            Text(
                text = stringResource(Res.string.event_freeze_screen_frozen_hint),
                style = MaterialTheme.typography.labelSmall,
                modifier = Modifier.padding(horizontal = 16.dp)
            )
        }
    }

    LazyColumn(state = lazyListState) {
        item {
            EventHeader(state)
        }
        items(state.tracks) { detailItem ->
            when (detailItem) {
                is EventDetailsItem.Header -> {
                    HeaderTitle(modifier = Modifier.padding(horizontal = 16.dp))
                }

                is EventDetailsItem.Item -> {
                    TrackCard(
                        trackCardModel = detailItem.track,
                        onClick = viewModel::onTrackClicked,
                        onMemberClicked = viewModel::onMemberClicked,
                        modifier = Modifier.padding(horizontal = 8.dp),
                    )
                }
            }
        }

        item {
            Spacer(Modifier.size(76.dp))
        }
    }
}

@Composable
fun EventHeader(state: EventDetailsState) {
    val date = state.date ?: return
    val spot = state.spot ?: return

    val formatted = DateFormatter.format(date, pattern = EVENT_DETAILS_FORMAT)

    Text(
        text = "$formatted - $spot",
        style = MaterialTheme.typography.titleLarge,
        color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f),
        modifier = Modifier.padding(start = 60.dp),
    )

    Spacer(modifier = Modifier.size(4.dp))

    state.userAcceptance?.let { userAcceptance ->
        Text(
            text = stringResource(
                Res.string.event_detail_screen_acceptance_title,
                userAcceptance.hard,
                userAcceptance.hardLimit,
            ),
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f),
            modifier = Modifier.padding(start = 60.dp),
        )

        Text(
            text = stringResource(
                Res.string.event_detail_screen_acceptance_optional,
                userAcceptance.weak,
            ),
            style = MaterialTheme.typography.titleSmall,
            color = MaterialTheme.colorScheme.outline.copy(alpha = 0.5f),
            modifier = Modifier.padding(start = 60.dp),
        )
    }

    Spacer(modifier = Modifier.size(16.dp))
}

@Composable
private fun HeaderTitle(modifier: Modifier) {
    Row(modifier = modifier.fillMaxWidth()) {
        Text(
            text = stringResource(Res.string.event_freeze_screen_backlog_header),
            style = MaterialTheme.typography.headlineMedium,
        )
    }
}

@Composable
internal fun ConfirmAcceptMemberDialog(
    trackName: String,
    artistName: String,
    instrumentName: String,
    onPositiveClicked: () -> Unit,
    onNegativeClicked: () -> Unit,
) {
    AlertDialog(
        title = {
            Text(text = stringResource(Res.string.event_detail_confirm_member_title))
        },
        text = {
            Text(
                text = stringResource(
                    Res.string.event_detail_confirm_member_message,
                    trackName,
                    artistName,
                    instrumentName
                )
            )
        },
        onDismissRequest = onNegativeClicked,
        confirmButton = {
            TextButton(
                onClick = {
                    onPositiveClicked()
                }
            ) {
                Text(stringResource(Res.string.dialog_ok))
            }
        },
        dismissButton = {
            TextButton(
                onClick = {
                    onNegativeClicked()
                }
            ) {
                Text(stringResource(Res.string.dialog_cancel))
            }
        },
        properties = DialogProperties(
            dismissOnBackPress = false,
            dismissOnClickOutside = false,
        )
    )
}

@Composable
internal fun ConfirmCancelMemberDialog(
    trackName: String,
    artistName: String,
    instrumentName: String,
    onPositiveClicked: () -> Unit,
    onNegativeClicked: () -> Unit,
) {
    AlertDialog(
        title = {
            Text(text = stringResource(Res.string.event_detail_confirm_member_cancellation_title))
        },
        text = {
            Text(
                text = stringResource(
                    Res.string.event_detail_confirm_member_cancellation_message,
                    trackName,
                    artistName,
                    instrumentName
                )
            )
        },
        onDismissRequest = onNegativeClicked,
        confirmButton = {
            TextButton(
                onClick = {
                    onPositiveClicked()
                }
            ) {
                Text(stringResource(Res.string.dialog_ok))
            }
        },
        dismissButton = {
            TextButton(
                onClick = {
                    onNegativeClicked()
                }
            ) {
                Text(stringResource(Res.string.dialog_cancel))
            }
        },
        properties = DialogProperties(
            dismissOnBackPress = false,
            dismissOnClickOutside = false,
        )
    )
}